/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  Stack,
  Autocomplete,
  Checkbox,
  ListItemText,
  Alert,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import FormikForm from "components/layout/FormikForm";
import { Form, FormikProps } from "formik";
import useHttp from "hooks/http.hook";
import {
  BillingEncounterInterface,
  AccountingInterface,
  HeaderEnum,
  PricingInterface,
  AccountingMovementInterface,
  QueryResultInterface,
} from "@malmur-technologies/medicalsystem-shared";
import { useTranslation } from "react-i18next";
import Button from "components/generals/Button";
import PermissionWrapper from "components/generals/PermissionWrapper";
import BillingEncounterTable from "components/tables/AppointmentBillingEncounterTable";
import { CoreAPI_URLs } from "constants/http.const";
import { EntityPermissions, PermissionsUser } from "types";
import { AccountingStatusEnum } from "@malmur-technologies/medicalsystem-shared";
import { getNewPatientAccountingValues } from "utils/model.utils";
import EventSlice, { MessageType, MessageState } from "store/slices/event.slice";
import FormField from "components/generals/FormInput";

/**
 * Props for the BillingEncounterForm component.
 * @interface BillingEncounterFormProps
 * @property {string} appointmentId - The appointment ID to create billing encounters for
 * @property {string} patientId - The patient ID associated with the billing
 * @property {() => void} onCloseAction - Callback when form is closed
 * @property {boolean} [insurance] - Flag indicating if billing is for insurance
 */
interface BillingEncounterFormProps {
  appointmentId: string;
  patientId: string;
  onCloseAction: () => void;
  insurance?: boolean;
  nonBillable?: boolean;
}

interface SelectedPricingItem {
  id: string;
  name: string;
  description: string;
  price: number;
  discountType?: string;
  discountValue?: number;
  uniqueId: string;
  cpt: string;
  isClaim: boolean;
  coInsuranceApplicable?: boolean;
  billingEncounterId?: string;
}

const accountingCreationCache = new Map<
  string,
  { promise: Promise<AccountingInterface>; timestamp: number }
>();

const CACHE_EXPIRY = 30000;
const MAX_RELOAD_ATTEMPTS = 3;
const RELOAD_DELAY = 2000;

const BillingEncounterForm: React.FC<BillingEncounterFormProps> = ({
  appointmentId,
  patientId,
  onCloseAction,
  insurance,
  nonBillable
}) => {
  const { t } = useTranslation();
  const [isHttpLoading, httpError, sendRequest] = useHttp();
  const dispatch = useDispatch();
  const { addErrorMessage, addSuccessMessage, addLoadingMessage, resetLoadingMessages } =
    EventSlice.actions;

  const [pricingOptions, setPricingOptions] = useState<PricingInterface[]>([]);
  const [selectedPricing, setSelectedPricing] = useState<SelectedPricingItem[]>([]);
  const [accounting, setAccounting] = useState<AccountingInterface | null>(null);
  const [tempAccountig, settempAccountig] = useState<boolean>(false);
  const [initialTotalAmount, setInitialTotalAmount] = useState<number>(0);
  const [initialAmountDue, setInitialAmountDue] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [amountDue, setAmountDue] = useState<number>(0);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [isDataReady, setIsDataReady] = useState<boolean>(false);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [reloadAttempts, setReloadAttempts] = useState<number>(0);
  const [isAutoReloading, setIsAutoReloading] = useState<boolean>(false);
  const [initializationKey, setInitializationKey] = useState<number>(0);
  const [accumulatedTotal, setAccumulatedTotal] = useState<number>(0);
  const [existingBillingEncounters, setExistingBillingEncounters] = useState<BillingEncounterInterface[]>([]);
  const [isLoadingExistingEncounters, setIsLoadingExistingEncounters] = useState<boolean>(false);
  const [originalSelectedPricing, setOriginalSelectedPricing] = useState<SelectedPricingItem[]>([]);
  const [searchInputValue, setSearchInputValue] = useState<string>("");

  const isSubmitting = useRef(false);
  const reloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLoading = isInitializing || !isDataReady || isAutoReloading;

  // Filter pricing options in real-time based on search input
  const filteredPricingOptions = useMemo(() => {
    if (!searchInputValue || searchInputValue.trim() === '') {
      return pricingOptions;
    }

    const searchLower = searchInputValue.toLowerCase().trim();
    return pricingOptions.filter((pricing) => {
      const cptMatch = pricing.cpt?.toLowerCase().includes(searchLower);
      const nameMatch = pricing.name?.toLowerCase().includes(searchLower);
      const descMatch = pricing.description?.toLowerCase().includes(searchLower);
      return cptMatch || nameMatch || descMatch;
    });
  }, [pricingOptions, searchInputValue]);

  useEffect(() => {
    let total = 0;
    selectedPricing.forEach((service) => {
      const discount =
        service.discountType === "Percentage"
          ? (Number(service.discountValue) / 100) * Number(service.price)
          : Number(service.discountValue) || 0;
      total += Number(service.price) - discount;
    });
    setAccumulatedTotal(total);
  }, [selectedPricing]);

  const handleError = (
    message: string,
    error?: any,
    messageType: MessageType = MessageType.ERROR
  ) => {
    const messagePayload: MessageState = {
      message,
      messageType,
      showError: true,
      code: error?.response?.status?.toString() || error?.code || undefined,
      id: crypto.randomUUID(),
    };
    dispatch(addErrorMessage(messagePayload));
    setComponentError(message);
  };

  const handleSuccess = (message: string) => {
    const messagePayload: MessageState = {
      message,
      messageType: MessageType.SUCCESS,
      id: crypto.randomUUID(),
    };
    dispatch(addSuccessMessage(messagePayload));
  };

  const handleLoading = (message: string) => {
    const messagePayload: MessageState = {
      message,
      messageType: MessageType.LOADING,
      id: crypto.randomUUID(),
    };
    dispatch(addLoadingMessage(messagePayload));
  };

  const generateUniqueId = () => {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const isPricingSelected = (pricingId: string) => {
    return selectedPricing.some((item) => item.id === pricingId);
  };

  const handlePricingToggle = (pricing: PricingInterface) => {
    const newItem: SelectedPricingItem = {
      id: pricing.id,
      name: pricing.name,
      description: pricing.description,
      price: pricing.price,
      discountType: "No Discount",
      discountValue: 0,
      uniqueId: generateUniqueId(),
      cpt: pricing.cpt,
      isClaim: false,
      coInsuranceApplicable: false,
    };
    setSelectedPricing((prev) => [...prev, newItem]);
  };

  const removePricingItem = async (uniqueId: string) => {
    const itemToRemove = selectedPricing.find((item) => item.uniqueId === uniqueId);

    if (itemToRemove?.billingEncounterId) {
      try {
        handleLoading(t("billingEncounterForm.deletingEncounter"));
        if (accounting?.id) {
          const accountingMovementsResponse = await sendRequest<QueryResultInterface<AccountingMovementInterface>>(
            CoreAPI_URLs.ACCOUNTING_MOVEMENT_GET_ALL,
            (res) => res,
            "GET",
            null,
            [{ header: HeaderEnum.PATIENT_ACCOUNT_NUMBER, value: accounting.id }]
          );

          const movements = accountingMovementsResponse?.data?.results || [];
          const relatedMovement = movements.find(
            (movement) => movement.billingEncounter?.id === itemToRemove.billingEncounterId
          );

          if (relatedMovement) {
            await sendRequest(
              CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS,
              null,
              "DELETE",
              null,
              [{ header: HeaderEnum.RESOURCE_ID, value: relatedMovement.id }],
              { successMessage: t("billingEncounterForm.successAccountingMovementDeleted") }
            );
          }
        }

        // Then, delete the billing encounter
        await sendRequest(
          CoreAPI_URLs.BILLING_ENCOUNTER_GENERAL_OPERATIONS,
          null,
          "DELETE",
          null,
          [{ header: HeaderEnum.RESOURCE_ID, value: itemToRemove.billingEncounterId }],
          { successMessage: t("billingEncounterForm.successAccountingMovementDeleted") }
        );

        handleSuccess(t("billingEncounterForm.successEncounterDeleted"));
        dispatch(resetLoadingMessages());
      } catch (error: any) {
        handleError(t("billingEncounterForm.errorDeletingEncounter"), error);
        dispatch(resetLoadingMessages());
        return; // Don't remove from UI if DB deletion failed
      }
    }

    // Remove from local list
    setSelectedPricing((prev) => prev.filter((item) => item.uniqueId !== uniqueId));
  };

  const cleanExpiredCache = () => {
    const now = Date.now();
    for (const [key, value] of accountingCreationCache.entries()) {
      if (now - value.timestamp > CACHE_EXPIRY) {
        accountingCreationCache.delete(key);
      }
    }
  };

  const fetchExistingBillingEncounters = async () => {
    try {
      setIsLoadingExistingEncounters(true);
      const response = await sendRequest<QueryResultInterface<BillingEncounterInterface>>(
        CoreAPI_URLs.BILLING_ENCOUNTER_GET_ALL,
        (res) => res,
        "GET",
        null,
        [{ header: HeaderEnum.APPOINTMENT_ID, value: appointmentId }]
      );

      const encounters = response?.data?.results || [];
      setExistingBillingEncounters(encounters);

      // Convert existing encounters to table format
      const existingItems: SelectedPricingItem[] = encounters.map((encounter) => {
        // Price should be the original pricing price
        // Discount stored in DB is a PERCENTAGE (0-100)
        const originalPrice = Number(encounter.pricing?.price) || 0;
        const discountPercentage = Number(encounter.discount) || 0;

        return {
          id: encounter.pricing?.id || "",
          name: encounter.pricingName || "",
          description: encounter.pricingDescription || "",
          price: originalPrice, // Original pricing price
          discountType: discountPercentage > 0 ? "Percentage" : "No Discount",
          discountValue: discountPercentage, // Show as PERCENTAGE for user
          uniqueId: `existing_${encounter.id}`,
          cpt: encounter.pricing?.cpt || "",
          isClaim: encounter.isClaim ?? true,
          coInsuranceApplicable: encounter.applyCoInsurance ?? false,
          billingEncounterId: encounter.id, // Mark as already saved
        };
      });

      setSelectedPricing(existingItems);
      // Save original copy to detect changes
      setOriginalSelectedPricing(structuredClone(existingItems));
    } catch (error: any) {
      console.error("Error loading existing billing encounters:", error);
      // Don't show critical error, just log
    } finally {
      setIsLoadingExistingEncounters(false);
    }
  };

  const resetFormState = () => {
    setAccounting(null);
    settempAccountig(false);
    setPricingOptions([]);
    setSelectedPricing([]);
    setInitialTotalAmount(0);
    setInitialAmountDue(0);
    setTotalAmount(0);
    setAmountDue(0);
    setIsInitializing(true);
    setIsDataReady(false);
    setComponentError(null);
    setIsAutoReloading(false);
    setExistingBillingEncounters([]);
    setOriginalSelectedPricing([]);
  };

  const scheduleAutoReload = () => {
    if (reloadAttempts >= MAX_RELOAD_ATTEMPTS) {
      handleError(t("billingEncounterForm.errorMaxReloadAttemptsReached"));
      return;
    }
    setIsAutoReloading(true);
    handleLoading(t("billingEncounterForm.loadingAutoReloading"));
    reloadTimeoutRef.current = setTimeout(() => {
      setReloadAttempts((prev) => prev + 1);
      setInitializationKey((prev) => prev + 1);
    }, RELOAD_DELAY);
  };

  const fetchAccount = async (): Promise<AccountingInterface | null> => {
    try {
      const response = await sendRequest<{ results: AccountingInterface }>(
        CoreAPI_URLs.ACCOUNTING_BY_PATIENT_ID,
        (res) => res,
        "GET",
        null,
        [{ header: HeaderEnum.RESOURCE_ID, value: patientId }]
      );
      const accounting = response?.data?.results || response?.data;
      return accounting || null;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  };

  const createNewAccount = async (): Promise<AccountingInterface | null> => {
    try {
      const newAccountData = getNewPatientAccountingValues(patientId);
      const accountPayload = {
        ...newAccountData,
        status: AccountingStatusEnum.OPEN,
        totalAmount: 0,
        amountDue: 0,
      };
      const createResponse = await sendRequest<{ results: AccountingInterface[] }>(
        CoreAPI_URLs.ACCOUNTING_GENERAL_OPERATIONS,
        (res) => res,
        "POST",
        JSON.stringify(accountPayload),
        [{ header: HeaderEnum.RESOURCE_ID, value: patientId }],
        { successMessage: t("billingEncounterForm.successAccountCreated") }
      );
      const accountsArray = createResponse?.data?.results || createResponse?.data;
      if (accountsArray && accountsArray.length > 0) {
        const createdAccount = accountsArray[0];
        return createdAccount;
      }
      return null;
    } catch (error: any) {
      const isDuplicateError = error?.response?.status === 409;
      if (isDuplicateError) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        try {
          const existingAccount = await sendRequest<{ results: AccountingInterface }>(
            CoreAPI_URLs.ACCOUNTING_BY_PATIENT_ID,
            (res) => res,
            "GET",
            null,
            [{ header: HeaderEnum.RESOURCE_ID, value: patientId }]
          );
          const accounting = existingAccount?.data?.results || existingAccount?.data;
          if (accounting) {
            return accounting;
          }
        } catch (fetchError) {
          console.error("Failed to fetch existing accounting after duplicate error:", fetchError);
        }
      }
      throw error;
    }
  };

  const fetchOrCreateAccount = async (): Promise<{
    accounting: AccountingInterface | null;
    wasCreated: boolean;
  }> => {
    try {
      cleanExpiredCache();
      const cached = accountingCreationCache.get(patientId);
      if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
        try {
          const accounting = await cached.promise;
          return { accounting, wasCreated: false };
        } catch (error) {
          accountingCreationCache.delete(patientId);
        }
      }

      let accounting = await fetchAccount();
      if (accounting) {
        return { accounting, wasCreated: false };
      }

      const creationPromise = createNewAccount();
      accountingCreationCache.set(patientId, {
        promise: creationPromise as Promise<AccountingInterface>,
        timestamp: Date.now(),
      });

      try {
        const result = await creationPromise;
        return { accounting: result, wasCreated: true };
      } finally {
        accountingCreationCache.delete(patientId);
      }
    } catch (error) {
      accountingCreationCache.delete(patientId);
      throw error;
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await sendRequest<{ results: PricingInterface[] }>(
        CoreAPI_URLs.PRICING_GET_ALL,
        (res) => res,
        "GET",
        null,
        [{ header: HeaderEnum.RESOURCE_ID, value: patientId }]
      );
      const pricings = response?.data?.results || [];
      setPricingOptions(pricings);
      return pricings;
    } catch (error) {
      console.error("Error fetching pricing:", error);
      setPricingOptions([]);
      throw error;
    }
  };

  const fetchLastAccountMovement = async (accountId: string) => {
    try {
      const url = `${CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS}/all`;
      const response = await sendRequest<{ results: AccountingMovementInterface[] }>(
        url,
        (res) => res,
        "GET",
        null,
        [{ header: HeaderEnum.PATIENT_ACCOUNT_NUMBER, value: accountId }]
      );
      const movements = response?.data?.results || response?.data || [];
      if (movements && movements.length > 0) {
        const lastMovement = movements[0];
        const totalAmount = lastMovement.totalAmount;
        const amountDue = lastMovement.amountDue;
        setInitialTotalAmount(totalAmount);
        setInitialAmountDue(amountDue);
        setTotalAmount(totalAmount);
        setAmountDue(amountDue);
        return { totalAmount, amountDue };
      } else {
        setInitialTotalAmount(0);
        setInitialAmountDue(0);
        setTotalAmount(0);
        setAmountDue(0);
        return { totalAmount: 0, amountDue: 0 };
      }
    } catch (error: any) {
      setInitialTotalAmount(0);
      setInitialAmountDue(0);
      setTotalAmount(0);
      setAmountDue(0);
      throw error;
    }
  };

  const initialize = async () => {
    try {
      handleLoading(t("billingEncounterForm.loadingInitializingForm"));
      dispatch(resetLoadingMessages());
      setIsInitializing(true);
      setComponentError(null);
      setIsDataReady(false);
      setIsAutoReloading(false);

      const { accounting: accountData, wasCreated } = await fetchOrCreateAccount();
      if (!accountData) {
        throw new Error(t("billingEncounterForm.failedfetch"));
      }

      setAccounting(accountData);
      settempAccountig(wasCreated);

      const [movementData, pricingData] = await Promise.all([
        fetchLastAccountMovement(accountData.id),
        fetchPricing(),
      ]);

      setIsDataReady(true);
      setIsInitializing(false);
      setIsAutoReloading(false);
      setReloadAttempts(0);
      dispatch(resetLoadingMessages());

      if (wasCreated) {
        handleSuccess(t("billingEncounterForm.successAccountCreatedAndFormRefreshed"));
        setTimeout(() => {
          setInitializationKey((prev) => prev + 1);
        }, 1000);
      }
    } catch (error: any) {
      setIsInitializing(false);
      setIsDataReady(false);
      setIsAutoReloading(false);
      const errorMessage = error?.message || t("billingEncounterForm.errorInitializingForm");
      handleError(errorMessage, error);
      dispatch(resetLoadingMessages());
      scheduleAutoReload();
    }
  };

  useEffect(() => {
    if (reloadTimeoutRef.current) {
      clearTimeout(reloadTimeoutRef.current);
      reloadTimeoutRef.current = null;
    }
    if (initializationKey > 0) {
      resetFormState();
    }
    initialize();
    fetchExistingBillingEncounters();

    return () => {
      accountingCreationCache.delete(patientId);
      dispatch(resetLoadingMessages());
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
        reloadTimeoutRef.current = null;
      }
    };
  }, [patientId, initializationKey]);

  useEffect(() => {
    if (httpError && !isSubmitting.current) {
      handleError((t("billingEncounterForm.requestErroHttp")), { message: httpError });
    }
  }, [httpError]);

  const handleCancel = async () => {
    if (reloadTimeoutRef.current) {
      clearTimeout(reloadTimeoutRef.current);
      reloadTimeoutRef.current = null;
    }
    if (tempAccountig && accounting) {
      try {
        handleLoading(t("billingEncounterForm.loadingDeletingTemporaryAccount"));
        await sendRequest(
          `${CoreAPI_URLs.ACCOUNTING_GENERAL_OPERATIONS}/${accounting.id}`,
          null,
          "DELETE",
          null,
          { successMessage: t("billingEncounterForm.successAccountingMovementDeleted") }
        );
        dispatch(resetLoadingMessages());
      } catch (error) {
        handleError(t("billingEncounterForm.errorDeletingTemporaryAccount"), error);
      }
    }
    onCloseAction();
  };

  const onSubmit = async () => {
    // Filter new and modified items
    const newPricingItems = selectedPricing.filter(item => !item.billingEncounterId);
    const modifiedPricingItems = selectedPricing.filter(item => {
      if (!item.billingEncounterId) return false;
      const original = originalSelectedPricing.find(o => o.uniqueId === item.uniqueId);
      if (!original) return false;

      // Convert both to absolute amount to compare correctly
      const originalDiscountAmount = original.discountType === "Percentage"
        ? (Number(original.discountValue) / 100) * Number(original.price)
        : Number(original.discountValue) || 0;

      const currentDiscountAmount = item.discountType === "Percentage"
        ? (Number(item.discountValue) / 100) * Number(item.price)
        : Number(item.discountValue) || 0;

      // Compare relevant fields
      return (
        original.price !== item.price ||
        Math.abs(originalDiscountAmount - currentDiscountAmount) > 0.01 ||
        original.isClaim !== item.isClaim ||
        original.coInsuranceApplicable !== item.coInsuranceApplicable
      );
    });

    if (newPricingItems.length === 0 && modifiedPricingItems.length === 0) {
      handleCancel();
      return;
    }

    isSubmitting.current = true;
    try {
      const hasNew = newPricingItems.length > 0;
      const hasModified = modifiedPricingItems.length > 0;

      if (hasNew && hasModified) {
        handleLoading(t("billingEncounterForm.loadingCreatingAndUpdating"));
      } else if (hasNew) {
        handleLoading(t("billingEncounterForm.loadingCreatingBillingEncounter"));
      } else {
        handleLoading(t("billingEncounterForm.loadingUpdatingBillingEncounter"));
      }

      let newTotalAmount = initialTotalAmount;
      let newAmountDue = initialAmountDue;

      // Update existing modified encounters
      for (const pricing of modifiedPricingItems) {
        // User can enter percentage OR absolute amount
        let discountAmount = 0;
        let discountPercentage = 0;

        if (pricing.discountType === "Percentage") {
          // User entered percentage (e.g., 15%), we save 15
          discountPercentage = Number(pricing.discountValue) || 0;
          discountAmount = (discountPercentage / 100) * Number(pricing.price);
        } else if (pricing.discountType === "Amount") {
          // User entered absolute amount (e.g., 150), convert to percentage
          discountAmount = Number(pricing.discountValue) || 0;
          discountPercentage = Number(pricing.price) > 0
            ? (discountAmount / Number(pricing.price)) * 100
            : 0;
        }

        // Calculate final price
        const finalPrice = Number(pricing.price) - discountAmount;

        const encounterUpdatePayload = {
          appointment: { id: appointmentId },
          pricing: { id: pricing.id },
          accounting: { id: accounting!.id },
          price: finalPrice,
          discount: discountPercentage, // Save as PERCENTAGE (0-100)
          pricingName: pricing.name?.trim() || "N/A",
          pricingDescription: pricing.description?.trim() || "N/A",
          isClaim: pricing.isClaim,
          applyCoInsurance: pricing.coInsuranceApplicable || false,
          paid: false
        };

        // Backend should automatically recalculate all affected accounting movements
        await sendRequest(
          CoreAPI_URLs.BILLING_ENCOUNTER_GENERAL_OPERATIONS,
          (response) => response,
          "PUT",
          JSON.stringify(encounterUpdatePayload),
          [{ header: HeaderEnum.RESOURCE_ID, value: pricing.billingEncounterId }],
          { successMessage: t("billingEncounterForm.successEncounterUpdated") }
        );

        newTotalAmount += finalPrice;
        newAmountDue += finalPrice;
      }

      // Create new encounters
      for (const pricing of newPricingItems) {
        // User can enter percentage OR absolute amount
        let discountAmount = 0;
        let discountPercentage = 0;

        if (pricing.discountType === "Percentage") {
          // User entered percentage (e.g., 15%), we save 15
          discountPercentage = Number(pricing.discountValue) || 0;
          discountAmount = (discountPercentage / 100) * Number(pricing.price);
        } else if (pricing.discountType === "Amount") {
          // User entered absolute amount (e.g., 150), convert to percentage
          discountAmount = Number(pricing.discountValue) || 0;
          discountPercentage = Number(pricing.price) > 0
            ? (discountAmount / Number(pricing.price)) * 100
            : 0;
        }

        // Calculate final price
        const finalPrice = Number(pricing.price) - discountAmount;

        const encounterPayload = {
          appointment: { id: appointmentId },
          pricing: { id: pricing.id },
          accounting: { id: accounting!.id },
          price: finalPrice,
          discount: discountPercentage, // Save as PERCENTAGE (0-100)
          pricingName: pricing.name?.trim() || "N/A",
          pricingDescription: pricing.description?.trim() || "N/A",
          isClaim: pricing.isClaim,
          applyCoInsurance: pricing.coInsuranceApplicable || false,
          paid: false
        };

        const billingResponse = await sendRequest(
          CoreAPI_URLs.BILLING_ENCOUNTER_GENERAL_OPERATIONS,
          (response) => response,
          "POST",
          JSON.stringify(encounterPayload),
          null,
          { successMessage: t("billingEncounterForm.successBillingEncounterCreated") }
        );

        const billingCreatedId = billingResponse?.data?.id || billingResponse?.data?.results?.id;
        if (!billingCreatedId) {
          handleError(
            `${t("billingEncounterForm.errorCreatingBillingEncounterFor")} ${pricing.name}`
          );
          continue;
        }

        newTotalAmount += finalPrice;
        newAmountDue += finalPrice;

        setTotalAmount(newTotalAmount);
        setAmountDue(newAmountDue);
      }

      settempAccountig(false);
      dispatch(resetLoadingMessages());

      // Success message based on what was done
      if (newPricingItems.length > 0 && modifiedPricingItems.length > 0) {
        handleSuccess(t("billingEncounterForm.successEncountersCreatedAndUpdated"));
      } else if (newPricingItems.length > 0) {
        handleSuccess(t("billingEncounterForm.successBillingEncounterCreated"));
      } else {
        handleSuccess(t("billingEncounterForm.successEncountersUpdated"));
      }

      // Reload existing billing encounters from DB
      await fetchExistingBillingEncounters();

      // Don't close modal, allow user to continue adding more billing encounters
      // onCloseAction();
    } catch (error: any) {
      handleError(t("billingEncounterForm.errorCreatingBillingEncounter"), error);
    } finally {
      isSubmitting.current = false;
      dispatch(resetLoadingMessages());
    }
  };

  const handleCheckboxClick = (
    option: PricingInterface,
    event: React.MouseEvent<HTMLButtonElement | HTMLInputElement>
  ) => {
    event.stopPropagation();
    if (!isPricingSelected(option.id)) {
      handlePricingToggle(option);
    }
  };
  if (isLoading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Typography>{t("billingEncounterForm.loadingInitializingForm")}</Typography>
      </div>
    );
  }

  if (componentError && !isAutoReloading) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {componentError}
        <Button onClick={() => setInitializationKey((prev) => prev + 1)} sx={{ ml: 2 }}>
          {t("common.retry")}
        </Button>
      </Alert>
    );
  }

  return (
    <FormikForm<Partial<BillingEncounterInterface>>
      initialValues={{
        pricing: { id: "" },
        price: 0,
        discount: 0,
        pricingName: "",
        pricingDescription: "",
      }}
      onSubmit={onSubmit}

    >
      {(formikProps: FormikProps<Partial<BillingEncounterInterface>>) => (
        <Form id="billingEncounterForm" autoComplete="off">
          <Stack
            direction="row"
            spacing={2}
            mb={2}
            className="billing-encounter__stack"
          >
            <Typography variant="caption">
              {t("billingEncounterForm.patient")}:{" "}
              {accounting?.patient?.person?.name + " " + accounting?.patient?.person?.lastname ||
                "N/A"}
            </Typography>
            <Typography variant="caption">
              {t("billingEncounterForm.totalAmount")}: ${totalAmount.toFixed(2)}
            </Typography>
            <Typography variant="caption">
              {t("billingEncounterForm.amountDue")}: ${amountDue.toFixed(2)}
            </Typography>
            <Typography variant="caption">
              {t("billingEncounterForm.pricingOptions")}: {pricingOptions.length}
            </Typography>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} xl={7} lg={8} id="cy-pricingFormContainer">
              <Autocomplete
                id="cy-pricing"
                multiple
                options={filteredPricingOptions}
                disableCloseOnSelect
                getOptionLabel={(option) => `${option.cpt} ${option.name}`}
                inputValue={searchInputValue}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason !== 'reset') {
                    setSearchInputValue(newInputValue);
                  }
                }}
                renderOption={(props, option, { selected }) => (
                  <li id="cy-pricing-item" {...props} onClick={() => handlePricingToggle(option)}>
                    <Grid container alignItems="center" gap={2}>
                      <FormField
                        type="number"
                        className="item-width-field"
                        name={`items_${option.id}`}
                        label={t("billingEncounterForm.items")}
                        value={selectedPricing.filter((item) => item.id === option.id).length}
                        disabled
                      />
                      <Button
                        color="primary"
                        size="small"
                        onClick={(event) => { event.stopPropagation(); handlePricingToggle(option); }}
                      >
                        {t("billingEncounterForm.addButton")}
                      </Button>

                      <ListItemText primary={
                        <div style={{ display: "flex", overflow: "hidden", whiteSpace: "nowrap" }}>
                          {pricingOptions.some(p => p.cpt) && <div style={{ minWidth: 90 }}>{option.cpt}</div>}
                          <div title={option.name} style={{ maxWidth: 90, textOverflow: "ellipsis" }}>{option.name}</div>
                        </div>}
                      />
                    </Grid>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("billingEncounterForm.selectPricing")}
                    placeholder={t("billingEncounterForm.selectPricing")}
                  />
                )}
                value={[]}
                noOptionsText={t("billingEncounterForm.noOptions")}
              />
            </Grid>
            <Grid item xs={12}>
              <BillingEncounterTable
                selectedPricing={selectedPricing}
                setSelectedPricing={setSelectedPricing}
                onRemoveItem={removePricingItem}
                insurance={insurance}
                nonBillable={nonBillable}
              />
            </Grid>
            <Grid item xs={12} display="flex" alignItems="right">
              <TextField
                label={t("billingEncounterForm.accumulatedTotal")}
                value={`$${accumulatedTotal.toFixed(2)}`}
                InputProps={{ readOnly: true, sx: { color: "green" } }}
                sx={{
                  width: "300px",
                  ml: "auto",
                  "& .MuiInputLabel-root": { color: "green" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "green" },
                    "&:hover fieldset": { borderColor: "darkgreen" },
                    "&.Mui-focused fieldset": { borderColor: "green" },
                  },
                }}
                fullWidth={false}
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={2} mt={2}>
            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].CREATE]}
            >
              <Button
                id="cy-submit-billing-encounter"
                type="submit"
                variant="contained"
                color="primary"
                disabled={selectedPricing.length === 0 || isSubmitting.current}
              >
                {t("billingEncounterForm.submit")}
              </Button>
            </PermissionWrapper>
            <Button id="cy-cancel-billing-encounter" variant="outlined" onClick={handleCancel}>
              {t("common.close")}
            </Button>
          </Stack>
        </Form>
      )}
    </FormikForm>
  );
};

export default BillingEncounterForm;
